import DOMPurify from 'dompurify'
import { DeepPartialMap, ValidationErrorType } from 'kea-forms'
import posthog from 'posthog-js'

import { dayjs } from 'lib/dayjs'
import { dateStringToDayJs } from 'lib/utils/dateFilters'
import { getAppContext } from 'lib/utils/getAppContext'
import {
    MAX_ITERATION_COUNT,
    NEW_SURVEY,
    NewSurvey,
    SURVEY_CREATED_SOURCE,
    SURVEY_RATING_SCALE,
} from 'scenes/surveys/constants'
import { SurveyRatingResults } from 'scenes/surveys/surveyLogic'

import {
    BasicSurveyQuestion,
    CyclotronJobInvocationGlobals,
    CyclotronJobFiltersType,
    EventPropertyFilter,
    FeatureFlagFilters,
    LinkSurveyQuestion,
    MultipleSurveyQuestion,
    PropertyFilterType,
    PropertyOperator,
    QuestionProcessedResponses,
    RatingSurveyQuestion,
    Survey,
    SurveyAppearance,
    SurveyDisplayConditions,
    SurveyEventName,
    SurveyEventProperties,
    SurveyQuestion,
    SurveyQuestionType,
    SurveyRates,
    SurveySchedule,
    SurveyStats,
    SurveyType,
} from '~/types'

const sanitizeConfig = { ADD_ATTR: ['target'] }

export function sanitizeHTML(html: string): string {
    return DOMPurify.sanitize(html, sanitizeConfig)
}

export function sanitizeColor(color: string | undefined): string | undefined {
    if (!color) {
        return undefined
    }

    // test if the color is valid by adding a # to the beginning of the string
    if (CSS.supports('color', `#${color}`)) {
        return `#${color}`
    }

    return color
}

export function validateCSSProperty(property: string, value: string | undefined): string | undefined {
    if (!value) {
        return undefined
    }
    const isValidCSSProperty = CSS.supports(property, value)
    return !isValidCSSProperty ? `${value} is not a valid property for ${property}.` : undefined
}

export function validateSurveyAppearance(
    appearance: SurveyAppearance,
    hasRatingQuestions: boolean,
    surveyType: SurveyType
): DeepPartialMap<SurveyAppearance, ValidationErrorType> {
    // API surveys are rendered by the customer, so PostHog's appearance CSS is not applied.
    // The Customization section is also hidden in the editor for API surveys (SurveyEdit.tsx),
    // so flagging appearance errors would route submitSurveyFailure to a non-existent section
    // and silently block saves.
    if (surveyType === SurveyType.API) {
        return {}
    }
    return {
        backgroundColor: validateCSSProperty('background-color', appearance.backgroundColor),
        borderColor: validateCSSProperty('border-color', appearance.borderColor),
        textColor: validateCSSProperty('color', appearance.textColor),
        inputBackground: validateCSSProperty('background-color', appearance.inputBackground),
        inputTextColor: validateCSSProperty('color', appearance.inputTextColor),
        // Only validate rating button colors if there's a rating question
        ...(hasRatingQuestions && {
            ratingButtonActiveColor: validateCSSProperty('background-color', appearance.ratingButtonActiveColor),
            ratingButtonColor: validateCSSProperty('background-color', appearance.ratingButtonColor),
        }),
        submitButtonColor: validateCSSProperty('background-color', appearance.submitButtonColor),
        submitButtonTextColor: validateCSSProperty('color', appearance.submitButtonTextColor),
        maxWidth: validateCSSProperty('width', appearance.maxWidth),
        boxPadding: validateCSSProperty('padding', appearance.boxPadding),
        boxShadow: validateCSSProperty('box-shadow', appearance.boxShadow),
        borderRadius: validateCSSProperty('border-radius', appearance.borderRadius),
        zIndex: validateCSSProperty('z-index', appearance.zIndex),
        widgetSelector:
            surveyType === SurveyType.Widget && appearance?.widgetType === 'selector' && !appearance.widgetSelector
                ? 'Please enter a CSS selector.'
                : undefined,
    }
}

export function getSurveyResponseKey(questionIndex: number): string {
    return questionIndex === 0
        ? SurveyEventProperties.SURVEY_RESPONSE
        : `${SurveyEventProperties.SURVEY_RESPONSE}_${questionIndex}`
}

export function getSurveyIdBasedResponseKey(questionId: string): string {
    return `${SurveyEventProperties.SURVEY_RESPONSE}_${questionId}`
}

type SurveyExampleContext = Pick<Survey, 'id' | 'name' | 'questions'> | null | undefined

function getExampleSurveyResponseValue(question: SurveyQuestion, index: number): string | string[] | undefined {
    switch (question.type) {
        case SurveyQuestionType.Open:
            return question.question || `Example answer ${index + 1}`
        case SurveyQuestionType.Rating:
            return String(question.scale >= 10 ? 9 : Math.min(question.scale, 4))
        case SurveyQuestionType.SingleChoice:
            return question.choices[0] || `Option ${index + 1}`
        case SurveyQuestionType.MultipleChoice:
            return question.choices.slice(0, Math.min(question.choices.length, 2))
        case SurveyQuestionType.Link:
            return undefined
    }
}

export function buildSurveyExampleInvocationGlobals({
    survey,
    projectId,
    projectName,
    projectUrl,
    source,
    timestamp = new Date().toISOString(),
    eventUuid = '00000000-0000-0000-0000-000000000000',
    distinctId = 'example-distinct-id',
    personId = 'person-id',
    personName = 'Jane Doe',
    personEmail = 'jane@example.com',
}: {
    survey: SurveyExampleContext
    projectId: number
    projectName: string
    projectUrl: string
    source?: CyclotronJobInvocationGlobals['source']
    timestamp?: string
    eventUuid?: string
    distinctId?: string
    personId?: string
    personName?: string
    personEmail?: string
}): CyclotronJobInvocationGlobals {
    const responseProperties = Object.fromEntries(
        (survey?.questions ?? [])
            .filter((question) => question.id && question.type !== SurveyQuestionType.Link)
            .map((question, index) => [
                getSurveyIdBasedResponseKey(question.id!),
                getExampleSurveyResponseValue(question, index),
            ])
            .filter(([, value]) => value !== undefined)
    )

    return {
        project: {
            id: projectId,
            name: projectName,
            url: projectUrl,
        },
        event: {
            event: SurveyEventName.SENT,
            uuid: eventUuid,
            distinct_id: distinctId,
            timestamp,
            elements_chain: '',
            properties: {
                [SurveyEventProperties.SURVEY_ID]: survey?.id && survey.id !== NEW_SURVEY.id ? survey.id : 'survey-id',
                $survey_name: survey?.name || 'Survey',
                [SurveyEventProperties.SURVEY_COMPLETED]: true,
                [SurveyEventProperties.SURVEY_SUBMISSION_ID]: 'survey-submission-id',
                ...responseProperties,
            },
            url: `${projectUrl}/events/${encodeURIComponent(eventUuid)}/${encodeURIComponent(timestamp)}`,
        },
        person: {
            id: personId,
            name: personName,
            url: `${projectUrl}/person/${encodeURIComponent(distinctId)}`,
            properties: {
                email: personEmail,
            },
        },
        groups: {},
        ...(source ? { source } : {}),
    }
}

// Helper function to generate the response field keys with proper typing
export const getResponseFieldWithId = (
    questionIndex: number,
    questionId?: string
): { indexBasedKey: string; idBasedKey: string | undefined } => {
    return {
        indexBasedKey: getSurveyResponseKey(questionIndex),
        idBasedKey: questionId ? getSurveyIdBasedResponseKey(questionId) : undefined,
    }
}

export function getSurveyResponseValue(
    eventProperties: Record<string, any>,
    questionIndex: number,
    questionId?: string
): any {
    const { indexBasedKey, idBasedKey } = getResponseFieldWithId(questionIndex, questionId)
    return (idBasedKey && eventProperties[idBasedKey]) ?? eventProperties[indexBasedKey]
}

export function sanitizeSurveyDisplayConditions(
    displayConditions?: SurveyDisplayConditions | null,
    surveyType?: SurveyType
): SurveyDisplayConditions | null {
    if (!displayConditions) {
        return null
    }

    if (surveyType === SurveyType.ExternalSurvey) {
        return {
            actions: {
                values: [],
            },
            events: {
                values: [],
            },
            deviceTypes: undefined,
            deviceTypesMatchType: undefined,
            linkedFlagVariant: undefined,
            seenSurveyWaitPeriodInDays: undefined,
            url: undefined,
            urlMatchType: undefined,
        }
    }

    const trimmedUrl = displayConditions.url?.trim()
    const trimmedSelector = displayConditions.selector?.trim()
    const trimmedLinkedFlagVariant = displayConditions.linkedFlagVariant?.trim()

    const sanitized: SurveyDisplayConditions = {
        ...displayConditions,
        ...(trimmedUrl && { url: trimmedUrl }),
        ...(trimmedSelector && { selector: trimmedSelector }),
        ...(trimmedLinkedFlagVariant && { linkedFlagVariant: trimmedLinkedFlagVariant }),
    }

    // Remove the original keys if they were empty after trimming
    if (!trimmedUrl) {
        delete sanitized.url
    }
    if (!trimmedSelector) {
        delete sanitized.selector
    }
    if (!trimmedLinkedFlagVariant) {
        delete sanitized.linkedFlagVariant
    }

    return sanitized
}

export function sanitizeSurveyAppearance(
    appearance?: SurveyAppearance | null,
    isPartialResponsesEnabled = false,
    surveyType?: SurveyType
): SurveyAppearance | null {
    if (!appearance) {
        return null
    }

    return {
        ...appearance,
        shuffleQuestions: isPartialResponsesEnabled ? false : appearance.shuffleQuestions,
        backgroundColor: sanitizeColor(appearance.backgroundColor),
        borderColor: sanitizeColor(appearance.borderColor),
        ratingButtonActiveColor: sanitizeColor(appearance.ratingButtonActiveColor),
        ratingButtonColor: sanitizeColor(appearance.ratingButtonColor),
        submitButtonColor: sanitizeColor(appearance.submitButtonColor),
        submitButtonTextColor: sanitizeColor(appearance.submitButtonTextColor),
        thankYouMessageHeader: sanitizeHTML(appearance.thankYouMessageHeader ?? ''),
        thankYouMessageDescription: sanitizeHTML(appearance.thankYouMessageDescription ?? ''),
        surveyPopupDelaySeconds:
            surveyType === SurveyType.ExternalSurvey ? undefined : appearance.surveyPopupDelaySeconds,
    }
}

export type NPSBreakdown = {
    total: number
    promoters: number
    passives: number
    detractors: number
    score: string
}

// NPS calculation constants
const NPS_SCALE_SIZE = 11 // 0-10 scale
const NPS_PROMOTER_MIN = 9 // 9-10 are promoters
const NPS_PASSIVE_MIN = 7 // 7-8 are passives. 0-6 are detractors but we don't need a variable for that.

interface NPSRawData {
    values: number[]
    total: number
}

/**
 * Extracts raw NPS data from processed survey data
 */
function extractNPSRawData(processedData: QuestionProcessedResponses): NPSRawData | null {
    if (
        !processedData?.data ||
        processedData.type !== SurveyQuestionType.Rating ||
        !Array.isArray(processedData.data) ||
        processedData.data.length !== NPS_SCALE_SIZE
    ) {
        return null
    }

    return {
        values: processedData.data.map((item) => item.value),
        total: processedData.totalResponses,
    }
}

/**
 * Extracts raw NPS data from legacy survey rating results
 */
function extractNPSRawDataFromLegacy(surveyRatingResults: SurveyRatingResults[number]): NPSRawData | null {
    if (!surveyRatingResults?.data || surveyRatingResults.data.length !== NPS_SCALE_SIZE) {
        return null
    }

    return {
        values: surveyRatingResults.data,
        total: surveyRatingResults.total,
    }
}

/**
 * Core NPS calculation logic - works with raw data arrays
 */
function calculateNPSFromRawData(rawData: NPSRawData): NPSBreakdown {
    if (rawData.total === 0) {
        return { total: 0, promoters: 0, passives: 0, detractors: 0, score: '0.0' }
    }

    const promoters = rawData.values.slice(NPS_PROMOTER_MIN, NPS_SCALE_SIZE).reduce((acc, curr) => acc + curr, 0)
    const passives = rawData.values.slice(NPS_PASSIVE_MIN, NPS_PROMOTER_MIN).reduce((acc, curr) => acc + curr, 0)
    const detractors = rawData.values.slice(0, NPS_PASSIVE_MIN).reduce((acc, curr) => acc + curr, 0)

    const score = ((promoters - detractors) / rawData.total) * 100

    return {
        total: rawData.total,
        promoters,
        passives,
        detractors,
        score: score.toFixed(1),
    }
}

export function calculateNpsBreakdownFromProcessedData(processedData: QuestionProcessedResponses): NPSBreakdown | null {
    const rawData = extractNPSRawData(processedData)
    return rawData ? calculateNPSFromRawData(rawData) : null
}

export function calculateNpsBreakdown(surveyRatingResults: SurveyRatingResults[number]): NPSBreakdown | null {
    const rawData = extractNPSRawDataFromLegacy(surveyRatingResults)
    return rawData ? calculateNPSFromRawData(rawData) : null
}

// Helper to escape special characters in SQL strings
function escapeSqlString(value: string): string {
    return value.replace(/['\\]/g, '\\$&')
}

export function getSurveyResponse(question: SurveyQuestion, index: number): string {
    // Delegate to the backend HogQL helper so survey response typing stays
    // consistent with PropertyDefinition metadata and materialized column rules.
    if (question.type === SurveyQuestionType.MultipleChoice) {
        return question.id
            ? `getSurveyResponse(${index}, '${question.id}', true)`
            : `getSurveyResponse(${index}, '', true)`
    }

    return question.id ? `getSurveyResponse(${index}, '${question.id}')` : `getSurveyResponse(${index})`
}

/**
 * Creates a HogQL expression for survey answer filters that handles both index-based and ID-based property keys
 * using OR logic between the alternative formats for each question.
 *
 * @param filters - The answer filters to convert to HogQL expressions
 * @param survey - The survey object (needed to access question IDs)
 * @returns A HogQL expression string that can be used in queries. If there are no filters, it returns an empty string.
 *
 * TODO: Consider leveraging the backend query builder instead of duplicating this logic in the frontend.
 * ClickHouse has powerful functions like match(), multiIf(), etc. that could be used more effectively.
 */
export function createAnswerFilterHogQLExpression(filters: EventPropertyFilter[], survey: Survey): string {
    if (!filters || !filters.length) {
        return ''
    }

    // Build the filter expression as a string
    let filterExpression = ''
    let hasValidFilter = false

    // Process each filter
    for (const filter of filters) {
        // Skip filters with empty or undefined values
        if (filter.value === undefined || filter.value === null || filter.value === '') {
            continue
        }

        // Skip empty arrays
        if (Array.isArray(filter.value) && filter.value.length === 0) {
            continue
        }

        // Skip ILIKE filters with empty search patterns
        if (
            filter.operator === 'icontains' &&
            (filter.value === '%' ||
                filter.value === '%%' ||
                (typeof filter.value === 'string' && filter.value.trim() === ''))
        ) {
            continue
        }

        // split the string '$survey_response_' and take the last part, as that's the question id
        const questionId = filter.key.split(`${SurveyEventProperties.SURVEY_RESPONSE}_`).at(-1)
        const question = survey.questions.find((question) => question.id === questionId)
        if (!questionId || !question) {
            continue
        }

        const questionIndex = survey.questions.findIndex((question) => question.id === questionId)

        // Create the condition for this filter
        let condition = ''
        const escapedValue = escapeSqlString(String(filter.value))

        // Handle different operators
        switch (filter.operator) {
            case 'exact':
            case 'is_not':
                if (Array.isArray(filter.value)) {
                    const valueList = filter.value.map((v) => `'${escapeSqlString(String(v))}'`).join(', ')
                    condition = `(${getSurveyResponse(question, questionIndex)} ${
                        filter.operator === 'is_not' ? 'NOT IN' : 'IN'
                    } (${valueList}))`
                } else {
                    condition = `(${getSurveyResponse(question, questionIndex)} ${
                        filter.operator === 'is_not' ? '!=' : '='
                    } '${escapedValue}')`
                }
                break
            case 'icontains':
                if (question.type !== SurveyQuestionType.MultipleChoice) {
                    condition = `(${getSurveyResponse(question, questionIndex)} ILIKE '%${escapedValue}%')`
                } else {
                    condition = `(arrayExists(x -> x ilike '%${escapedValue}%', ${getSurveyResponse(question, questionIndex)}))`
                }
                break
            case 'not_icontains':
                if (question.type !== SurveyQuestionType.MultipleChoice) {
                    condition = `(NOT ${getSurveyResponse(question, questionIndex)} ILIKE '%${escapedValue}%')`
                } else {
                    condition = `(NOT arrayExists(x -> x ilike '%${escapedValue}%', ${getSurveyResponse(question, questionIndex)}))`
                }
                break
            case 'regex':
                if (question.type !== SurveyQuestionType.MultipleChoice) {
                    condition = `(match(${getSurveyResponse(question, questionIndex)}, '${escapedValue}'))`
                } else {
                    condition = `(arrayExists(x -> match(x, '${escapedValue}'), ${getSurveyResponse(question, questionIndex)}))`
                }
                break
            case 'not_regex':
                if (question.type !== SurveyQuestionType.MultipleChoice) {
                    condition = `(NOT match(${getSurveyResponse(question, questionIndex)}, '${escapedValue}'))`
                } else {
                    condition = `(NOT arrayExists(x -> match(x, '${escapedValue}'), ${getSurveyResponse(question, questionIndex)}))`
                }
                break
            // Add more operators as needed
            default:
                continue // Skip unsupported operators
        }

        // Add this condition to the overall expression
        if (condition) {
            if (hasValidFilter) {
                filterExpression += ' AND '
            }
            filterExpression += condition
            hasValidFilter = true
        }
    }

    return hasValidFilter ? `AND ${filterExpression}` : ''
}

export function isSurveyRunning(survey: Pick<Survey, 'start_date' | 'end_date'>): boolean {
    return !!(survey.start_date && !survey.end_date)
}

// Auto-submit only makes sense for questions where a single selection is a complete
// answer: any rating, or a single-choice question without a free-text "open" option.
export function canQuestionSkipSubmitButton(
    question: SurveyQuestion
): question is RatingSurveyQuestion | MultipleSurveyQuestion {
    return (
        question.type === SurveyQuestionType.Rating ||
        (question.type === SurveyQuestionType.SingleChoice && !question.hasOpenChoice)
    )
}

// Some fields can only be edited in the full editor — opening such a survey
// in the wizard would hide those values from the user, so we route them to
// the full editor regardless of their general editor preference. Keep this
// list in sync with what the wizard's steps actually expose.
export function canUseSurveyWizard(survey: Survey | NewSurvey): boolean {
    if (survey.type !== SurveyType.Popover) {
        return false
    }
    // SurveySchedule.Always — the wizard offers Once + recurring frequencies, but not "every time
    // the display conditions are met". Keep Always surveys in the legacy editor where the option
    // is actually visible, so the wizard never silently misrepresents the cadence.
    if (survey.schedule === SurveySchedule.Always) {
        return false
    }
    // Adaptive sampling — WhenStep exposes a simple responses_limit but not
    // the adaptive sampling controls
    if (survey.response_sampling_limit || survey.response_sampling_start_date) {
        return false
    }
    // Property-based targeting filters — WhereStep handles linked_flag
    // (release conditions) but not targeting_flag_filters
    if (survey.targeting_flag_filters && Object.keys(survey.targeting_flag_filters).length > 0) {
        return false
    }
    return true
}

export function doesSurveyRepeatOnEveryEvent(survey: Pick<Survey, 'conditions'>): boolean {
    return !!(survey.conditions?.events?.repeatedActivation && (survey.conditions?.events?.values?.length ?? 0) > 0)
}

export interface RecurringSurveyScheduleInfo {
    /** Total number of days the survey runs from its launch date before auto-closing. */
    totalDurationDays: number
    /** The date the survey will automatically close, or null if it hasn't been launched yet. */
    autoCloseDate: dayjs.Dayjs | null
}

/**
 * A recurring survey ("Repeat on a schedule") auto-closes once its final iteration window has passed.
 * The last iteration starts on `start_date + (count - 1) * frequency` days and lasts `frequency` more days,
 * so the survey runs for `count * frequency` days total and closes at the end of that span.
 * Mirrors the backend logic in posthog/tasks/update_survey_iteration.py, which computes iteration windows
 * on the UTC calendar day — so we do the arithmetic in UTC too.
 *
 * Returns null once the survey has already ended: it then shows its real end date, so a projected one would
 * only contradict it.
 */
export function getRecurringSurveyScheduleInfo(
    survey: Pick<Survey, 'schedule' | 'iteration_count' | 'iteration_frequency_days' | 'start_date' | 'end_date'>
): RecurringSurveyScheduleInfo | null {
    const count = survey.iteration_count
    const frequency = survey.iteration_frequency_days
    if (
        survey.schedule !== SurveySchedule.Recurring ||
        survey.end_date ||
        !count ||
        !frequency ||
        count < 1 ||
        frequency < 1
    ) {
        return null
    }
    // The backend caps the generated iteration windows at MAX_ITERATION_COUNT, so anything above that never
    // extends the schedule — mirror the cap here to match the real close date.
    const effectiveCount = Math.min(count, MAX_ITERATION_COUNT)
    const totalDurationDays = effectiveCount * frequency
    const autoCloseDate = survey.start_date ? dayjs.utc(survey.start_date).add(totalDurationDays, 'day') : null
    return { totalDurationDays, autoCloseDate }
}

export function doesSurveyHaveDisplayConditions(survey: Survey | NewSurvey): boolean {
    const conditions = sanitizeSurveyDisplayConditions(survey.conditions)
    if (!conditions) {
        return false
    }

    // check string fields
    if (conditions.url) {
        return true
    }

    if (conditions.selector) {
        return true
    }

    if (conditions.linkedFlagVariant) {
        return true
    }

    // check numeric fields
    if (conditions.seenSurveyWaitPeriodInDays !== undefined && conditions.seenSurveyWaitPeriodInDays !== null) {
        return true
    }

    // check array fields
    if (conditions.deviceTypes && conditions.deviceTypes.length > 0) {
        return true
    }

    // check enum fields
    if (conditions.urlMatchType !== undefined && conditions.urlMatchType !== null) {
        return true
    }

    if (conditions.deviceTypesMatchType !== undefined && conditions.deviceTypesMatchType !== null) {
        return true
    }

    // check complex object fields
    if (conditions.actions && conditions.actions.values && conditions.actions.values.length > 0) {
        return true
    }

    if (conditions.events && conditions.events.values && conditions.events.values.length > 0) {
        return true
    }

    if (conditions.events?.repeatedActivation !== undefined && conditions.events.repeatedActivation !== null) {
        return true
    }

    return false
}

export function buildSurveyOptionalBooleanPropertyFilter(
    propertyName: SurveyEventProperties,
    excludedValue: 'true' | 'false'
): string {
    return `coalesce(JSONExtractString(properties, '${propertyName}'), '') != '${excludedValue}'`
}

export function buildPartialResponsesFilter(survey: Survey, dateRange?: SurveyDateRange | null): string {
    if (!survey.enable_partial_responses) {
        return `AND ${buildSurveyOptionalBooleanPropertyFilter(SurveyEventProperties.SURVEY_COMPLETED, 'false')}`
    }

    const { fromDate, toDate } = getResolvedSurveyDateRange(survey, dateRange)

    return `AND uuid in (
        SELECT
            argMax(uuid, timestamp)
        FROM events
        WHERE and(
            equals(event, '${SurveyEventName.SENT}'),
            equals(properties.\`${SurveyEventProperties.SURVEY_ID}\`, '${survey.id}'),
            greaterOrEquals(timestamp, '${fromDate}'),
            lessOrEquals(timestamp, '${toDate}')
        )
        GROUP BY
            if(
                isNotNull(properties.\`${SurveyEventProperties.SURVEY_SUBMISSION_ID}\`)
                    AND properties.\`${SurveyEventProperties.SURVEY_SUBMISSION_ID}\` != '',
                properties.\`${SurveyEventProperties.SURVEY_SUBMISSION_ID}\`,
                toString(uuid)
            )
    ) --- Filter to ensure we only get one response per ${SurveyEventProperties.SURVEY_SUBMISSION_ID}`
}

export interface SurveyQueryFilters {
    timestampFilter: string
    answerFilterHogQLExpression: string
    archivedResponsesFilter: string
}

export interface OpenEndedColumnMap {
    [questionId: string]: {
        columnIndex: number
        questionIndex: number
        type: SurveyQuestionType.Open | SurveyQuestionType.SingleChoice | SurveyQuestionType.MultipleChoice
    }
}

export function buildAggregateQuery(
    survey: Survey,
    filters: SurveyQueryFilters,
    dateRange?: SurveyDateRange | null
): string | null {
    const dedupFilter = buildPartialResponsesFilter(survey, dateRange)
    const branches: string[] = []

    const baseWhere = `event = '${SurveyEventName.SENT}'
        AND properties.\`${SurveyEventProperties.SURVEY_ID}\` = '${survey.id}'
        ${filters.timestampFilter}
        ${filters.answerFilterHogQLExpression}
        ${filters.archivedResponsesFilter}
        ${dedupFilter}
        AND {filters}`

    for (const [index, question] of survey.questions.entries()) {
        if (!question.id || question.type === SurveyQuestionType.Link) {
            continue
        }

        const responseExpr = getSurveyResponse(question, index)

        if (question.type === SurveyQuestionType.Rating || question.type === SurveyQuestionType.SingleChoice) {
            branches.push(`SELECT '${question.id}' AS question_id,
                ${responseExpr} AS label,
                count() AS cnt
            FROM events
            WHERE ${baseWhere} AND isNotNull(${responseExpr})
            GROUP BY label`)

            if (question.type === SurveyQuestionType.SingleChoice && question.optional) {
                branches.push(`SELECT '${question.id}' AS question_id,
                    '__no_response__' AS label,
                    count() AS cnt
                FROM events
                WHERE ${baseWhere} AND ${responseExpr} = ''`)
            }
        } else if (question.type === SurveyQuestionType.MultipleChoice) {
            branches.push(`SELECT '${question.id}' AS question_id,
                trim(BOTH '"\\'' FROM arrayJoin(${responseExpr})) AS label,
                count() AS cnt
            FROM events
            WHERE ${baseWhere}
            GROUP BY label HAVING isNotNull(label) AND label != ''`)

            branches.push(`SELECT '${question.id}' AS question_id,
                '__total__' AS label,
                count() AS cnt
            FROM events
            WHERE ${baseWhere} AND length(${responseExpr}) > 0`)

            if (question.optional) {
                branches.push(`SELECT '${question.id}' AS question_id,
                    '__no_response__' AS label,
                    count() AS cnt
                FROM events
                WHERE ${baseWhere} AND length(${responseExpr}) = 0`)
            }
        } else if (question.type === SurveyQuestionType.Open) {
            branches.push(`SELECT '${question.id}' AS question_id,
                '__total__' AS label,
                count() AS cnt
            FROM events
            WHERE ${baseWhere} AND isNotNull(${responseExpr})`)
        }
    }

    if (branches.length === 0) {
        return null
    }

    return `SELECT question_id, label, cnt FROM (${branches.join('\nUNION ALL\n')}) LIMIT 50000`
}

export function buildOpenEndedQuery(
    survey: Survey,
    filters: SurveyQueryFilters,
    dateRange?: SurveyDateRange | null,
    limit: number = 50000
): { query: string; columnMap: OpenEndedColumnMap } | null {
    const dedupFilter = buildPartialResponsesFilter(survey, dateRange)
    const openColumns: string[] = []
    const columnMap: OpenEndedColumnMap = {}
    let columnIndex = 0

    for (const [index, question] of survey.questions.entries()) {
        if (!question.id || question.type === SurveyQuestionType.Link) {
            continue
        }

        const isOpen = question.type === SurveyQuestionType.Open
        const hasOpenChoice =
            (question.type === SurveyQuestionType.SingleChoice ||
                question.type === SurveyQuestionType.MultipleChoice) &&
            (question as MultipleSurveyQuestion).hasOpenChoice

        if (isOpen || hasOpenChoice) {
            openColumns.push(`${getSurveyResponse(question, index)} AS q${index}_response`)
            columnMap[question.id] = { columnIndex, questionIndex: index, type: question.type }
            columnIndex++
        }
    }

    if (openColumns.length === 0) {
        return null
    }

    const query = `SELECT
            ${openColumns.join(',\n')},
            events.distinct_id,
            events.timestamp,
            events.properties.$session_id
        FROM events
        WHERE event = '${SurveyEventName.SENT}'
            AND properties.${SurveyEventProperties.SURVEY_ID} = '${survey.id}'
            ${filters.timestampFilter}
            ${filters.answerFilterHogQLExpression}
            ${filters.archivedResponsesFilter}
            ${dedupFilter}
            AND {filters}
        ORDER BY events.timestamp DESC
        LIMIT ${limit}`

    return { query, columnMap }
}

interface SanitizeSurveyOptions {
    keepEmptyConditions?: boolean
}

export function sanitizeSurvey(survey: Partial<Survey>, options?: SanitizeSurveyOptions): Partial<Survey> {
    const sanitizedQuestions =
        survey.questions?.map((question) => {
            const sanitized = {
                ...question,
                question: sanitizeHTML(question.question ?? ''),
                description: sanitizeHTML(question.description ?? ''),
            }
            if (
                (sanitized.type === SurveyQuestionType.SingleChoice ||
                    sanitized.type === SurveyQuestionType.MultipleChoice) &&
                sanitized.choices
            ) {
                sanitized.choices = sanitized.choices.map((choice) => choice.trim())
            }
            // Drop a stale auto-submit flag if the question is no longer eligible for it
            // (e.g. an open-ended choice was added, or the type was switched).
            if ('skipSubmitButton' in sanitized && !canQuestionSkipSubmitButton(sanitized)) {
                delete (sanitized as { skipSubmitButton?: boolean }).skipSubmitButton
            }
            return sanitized
        }) || []

    const sanitizedAppearance = sanitizeSurveyAppearance(
        survey.appearance,
        survey.enable_partial_responses ?? false,
        survey.type
    )

    // Remove widget-specific fields if survey type is not Widget
    if (survey.type !== SurveyType.Widget && sanitizedAppearance) {
        delete sanitizedAppearance.widgetType
        delete sanitizedAppearance.widgetLabel
        delete sanitizedAppearance.widgetColor
    }

    const conditions = sanitizeSurveyDisplayConditions(survey.conditions, survey.type)
    const sanitized: Partial<Survey> = {
        ...survey,
        conditions: conditions,
        questions: sanitizedQuestions,
        appearance: sanitizedAppearance,
    }

    if (survey.type === SurveyType.ExternalSurvey) {
        sanitized.remove_targeting_flag = true
        sanitized.linked_flag_id = null
        sanitized.targeting_flag_filters = undefined
    }

    if (options?.keepEmptyConditions !== true && (!conditions || Object.keys(conditions).length === 0)) {
        delete sanitized.conditions
    }
    if (!sanitizedAppearance || Object.keys(sanitizedAppearance).length === 0) {
        delete sanitized.appearance
    }

    return sanitized
}

export function calculateSurveyRates(stats: SurveyStats | null): SurveyRates {
    const defaultRates: SurveyRates = {
        response_rate: 0.0,
        dismissal_rate: 0.0,
        unique_users_response_rate: 0.0,
        unique_users_dismissal_rate: 0.0,
    }

    if (!stats) {
        return defaultRates
    }

    const shownCount = stats[SurveyEventName.SHOWN].total_count
    if (shownCount > 0) {
        const sentCount = stats[SurveyEventName.SENT].total_count
        const dismissedCount = stats[SurveyEventName.DISMISSED].total_count
        const uniqueUsersShownCount = stats[SurveyEventName.SHOWN].unique_persons
        const uniqueUsersSentCount = stats[SurveyEventName.SENT].unique_persons
        const uniqueUsersDismissedCount = stats[SurveyEventName.DISMISSED].unique_persons

        return {
            response_rate: parseFloat(((sentCount / shownCount) * 100).toFixed(2)),
            dismissal_rate: parseFloat(((dismissedCount / shownCount) * 100).toFixed(2)),
            unique_users_response_rate: parseFloat(((uniqueUsersSentCount / uniqueUsersShownCount) * 100).toFixed(2)),
            unique_users_dismissal_rate: parseFloat(
                ((uniqueUsersDismissedCount / uniqueUsersShownCount) * 100).toFixed(2)
            ),
        }
    }
    return defaultRates
}

export function captureMaxAISurveyCreationException(error?: string, source?: SURVEY_CREATED_SOURCE): void {
    posthog.captureException(error || 'Undefined error when creating MaxAI survey', {
        action: 'max-ai-survey-creation-failed',
        source: source,
    })
}

export const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

function getTeamTimezone(): string {
    return getAppContext()?.current_team?.timezone || 'UTC'
}

export function getSurveyStartDateForQuery(
    survey: Pick<Survey, 'created_at'> & Partial<Pick<Survey, 'start_date'>>
): string {
    const tz = getTeamTimezone()
    return dayjs
        .tz(survey.start_date ?? survey.created_at, tz)
        .startOf('day')
        .format(DATE_FORMAT)
}

export function getSurveyEndDateForQuery(survey: Pick<Survey, 'end_date'>): string {
    const tz = getTeamTimezone()
    return survey.end_date
        ? dayjs.tz(survey.end_date, tz).endOf('day').format(DATE_FORMAT)
        : dayjs.tz(undefined, tz).endOf('day').format(DATE_FORMAT)
}

export interface SurveyDateRange {
    date_from: string | null
    date_to: string | null
}

export function getResolvedSurveyDateRange(
    survey: Pick<Survey, 'created_at' | 'end_date'> & Partial<Pick<Survey, 'start_date'>>,
    dateRange?: SurveyDateRange | null
): { fromDate: string; toDate: string } {
    let fromDate = getSurveyStartDateForQuery(survey)
    let toDate = getSurveyEndDateForQuery(survey)

    // date_from only is valid ("from custom date until now")
    // date_to only is ignored to avoid impossible ranges
    if (dateRange?.date_from) {
        const tz = getTeamTimezone()
        fromDate = dateStringToDayJs(dateRange.date_from, tz)?.startOf('day').format(DATE_FORMAT) ?? fromDate

        if (dateRange.date_to) {
            toDate = dateStringToDayJs(dateRange.date_to, tz)?.endOf('day').format(DATE_FORMAT) ?? toDate
        }
    }

    return { fromDate, toDate }
}

export function buildSurveyTimestampFilter(
    survey: Pick<Survey, 'created_at' | 'end_date'> & Partial<Pick<Survey, 'start_date'>>,
    dateRange?: SurveyDateRange | null
): string {
    const { fromDate, toDate } = getResolvedSurveyDateRange(survey, dateRange)

    return `AND timestamp >= '${fromDate}'
    AND timestamp <= '${toDate}'`
}

export function getExpressionCommentForQuestion(
    q: BasicSurveyQuestion | LinkSurveyQuestion | RatingSurveyQuestion | MultipleSurveyQuestion,
    questionIndex: number
): string {
    const question = q.question.trim()
    if (question.length > 0) {
        // This is appended after `--` in the generated HogQL, and HogQL `--` comments are
        // single-line. Collapse any newlines so multi-line question text can't leak past the
        // comment and break the query (e.g. a stray non-ASCII char -> "Unexpected character").
        return question.replace(/\s*[\r\n]+\s*/g, ' ')
    }
    return `Question ${questionIndex + 1}`
}

export function getSurveyForFeatureFlagVariant(variantKey: string, surveys?: Survey[]): Survey | undefined {
    return surveys?.find((survey) => survey.conditions?.linkedFlagVariant === variantKey)
}

export function duplicateExistingSurvey(survey: Survey | NewSurvey): Partial<Survey> {
    return {
        ...survey,
        questions: survey.questions.map((question) => ({
            ...question,
            id: undefined,
        })),
        id: NEW_SURVEY.id,
        name: `${survey.name} (duplicated at ${dayjs().format('YYYY-MM-DD HH:mm:ss')})`,
        archived: false,
        start_date: null,
        end_date: null,
        targeting_flag_filters: survey.targeting_flag?.filters ?? NEW_SURVEY.targeting_flag_filters,
        linked_flag_id: survey.linked_flag?.id ?? NEW_SURVEY.linked_flag_id,
    }
}

export const isThumbQuestion = (question: SurveyQuestion): boolean => {
    return (
        question.type === SurveyQuestionType.Rating &&
        question.display === 'emoji' &&
        question.scale === SURVEY_RATING_SCALE.THUMB_2_POINT
    )
}

/**
 * A 2-point rating question always represents a binary thumbs up / thumbs down regardless of `display`,
 * so we render the icon + label in response views to make the value readable at a glance.
 */
export const isScaleTwoRating = (question: SurveyQuestion): boolean => {
    return question.type === SurveyQuestionType.Rating && question.scale === SURVEY_RATING_SCALE.THUMB_2_POINT
}

/**
 * Splits text pasted into a choice input on newlines or tabs (spreadsheet rows).
 * Returns the merged choices array, or `null` if there's nothing to split (the caller
 * should let the paste fall through to the default input behavior).
 *
 * Always keeps the open-ended ("Other") entry as the last item when `hasOpenChoice`
 * is true — including when the paste happens into the open-ended slot itself.
 */
export function splitChoicesOnPaste(
    pasted: string,
    choices: string[],
    choiceIndex: number,
    hasOpenChoice: boolean
): string[] | null {
    const segments = pasted
        .split(/[\n\t]+/)
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0)

    if (segments.length <= 1) {
        return null
    }

    const openTail = hasOpenChoice ? [choices[choices.length - 1]] : []
    const head = choices.slice(0, choiceIndex)
    const tailStart = choiceIndex + 1
    const tailEnd = hasOpenChoice ? choices.length - 1 : choices.length
    const tail = choices.slice(tailStart, tailEnd)
    return [...head, ...segments, ...tail, ...openTail]
}

export type SurveyConditionType =
    | 'url'
    | 'selector'
    | 'device'
    | 'events'
    | 'actions'
    | 'flag'
    | 'targeting'
    | 'wait_period'

export interface SurveyConditionSummary {
    type: SurveyConditionType
    label: string
    value: string
}

export interface SurveyCollectionLimitSummary {
    label: 'Response limit' | 'Sampling limit'
    value: string
}

export function getSurveyTargetingFilters(survey: Survey | NewSurvey): FeatureFlagFilters | undefined {
    if (survey.targeting_flag_filters) {
        return survey.targeting_flag_filters
    }

    return survey.targeting_flag?.filters || undefined
}

export function getSurveyAudienceRuleCount(filters?: FeatureFlagFilters | null): number {
    return filters?.groups.reduce((count, group) => count + (group.properties?.length ?? 0), 0) ?? 0
}

export function getSurveyAudienceRolloutPercentage(filters?: FeatureFlagFilters | null): number | null {
    if (!filters || filters.groups.length !== 1) {
        return null
    }

    return filters.groups[0].rollout_percentage ?? 100
}

export function isSimpleSurveyAudienceTargeting(filters?: FeatureFlagFilters | null): boolean {
    if (!filters) {
        return true
    }

    if (filters.groups.length !== 1 || filters.aggregation_group_type_index != null || filters.feature_enrollment) {
        return false
    }

    if (filters.multivariate?.variants?.length) {
        return false
    }

    const [group] = filters.groups

    if (group.aggregation_group_type_index != null || group.variant != null) {
        return false
    }

    return (group.properties || []).every(
        (property) => property.type === PropertyFilterType.Person || property.type === PropertyFilterType.Cohort
    )
}

export function getSurveyAudienceSummaryValue(survey: Survey | NewSurvey): string | null {
    const filters = getSurveyTargetingFilters(survey)

    if (!filters) {
        return null
    }

    if (!isSimpleSurveyAudienceTargeting(filters)) {
        return 'Advanced audience targeting'
    }

    const ruleCount = getSurveyAudienceRuleCount(filters)
    const rolloutPercentage = getSurveyAudienceRolloutPercentage(filters)
    const isPartialRollout = rolloutPercentage != null && rolloutPercentage < 100

    if (ruleCount === 0 && !isPartialRollout) {
        return null
    }

    if (ruleCount === 0) {
        return `${rolloutPercentage}% of matching users`
    }

    if (isPartialRollout) {
        return `${ruleCount} audience rule${ruleCount === 1 ? '' : 's'} · ${rolloutPercentage}% shown`
    }

    return `${ruleCount} audience rule${ruleCount === 1 ? '' : 's'}`
}

export function getSurveyCollectionLimitSummary(survey: Survey | NewSurvey): SurveyCollectionLimitSummary | null {
    if (survey.responses_limit && survey.responses_limit > 0) {
        return {
            label: 'Response limit',
            value: String(survey.responses_limit),
        }
    }

    if (
        survey.response_sampling_limit &&
        survey.response_sampling_limit > 0 &&
        survey.response_sampling_interval &&
        survey.response_sampling_interval > 0 &&
        survey.response_sampling_interval_type
    ) {
        return {
            label: 'Sampling limit',
            value: `${survey.response_sampling_limit} / ${survey.response_sampling_interval} ${survey.response_sampling_interval_type}`,
        }
    }

    return null
}

export function getSurveyDisplayConditionsSummary(survey: Survey | NewSurvey): SurveyConditionSummary[] {
    const parts: SurveyConditionSummary[] = []
    const conditions = survey.conditions

    if (conditions?.url) {
        parts.push({
            type: 'url',
            label: 'URL',
            value: `${conditions.urlMatchType === 'exact' ? 'is' : 'contains'} "${conditions.url}"`,
        })
    }
    if (conditions?.selector) {
        parts.push({ type: 'selector', label: 'Selector', value: conditions.selector })
    }
    if (conditions?.deviceTypes?.length) {
        parts.push({ type: 'device', label: 'Device', value: conditions.deviceTypes.join(', ') })
    }
    if ((conditions?.events?.values?.length ?? 0) > 0) {
        parts.push({
            type: 'events',
            label: 'Events',
            value: conditions!.events!.values.map((e) => e.name).join(', '),
        })
    }
    if ((conditions?.actions?.values?.length ?? 0) > 0) {
        parts.push({
            type: 'actions',
            label: 'Actions',
            value: conditions!.actions!.values.map((a) => a.name).join(', '),
        })
    }
    if (survey.linked_flag?.key) {
        parts.push({ type: 'flag', label: 'Feature flag', value: survey.linked_flag.key })
    } else if (survey.linked_flag_id) {
        parts.push({ type: 'flag', label: 'Feature flag', value: 'Linked' })
    }
    const audienceSummary = getSurveyAudienceSummaryValue(survey)
    if (audienceSummary) {
        parts.push({ type: 'targeting', label: 'Targeting', value: audienceSummary })
    }
    if (conditions?.seenSurveyWaitPeriodInDays) {
        parts.push({
            type: 'wait_period',
            label: 'Wait period',
            value: `${conditions.seenSurveyWaitPeriodInDays} days since last survey`,
        })
    }

    return parts
}

/**
 * With partial responses off, posthog-js has no partial submission to distinguish a complete one
 * from, so it never sets `$survey_completed` and requiring `= true` matches nothing. Accept the
 * property being absent as completed too, the same way the response summary counts them
 * (`enable_partial_responses` branch in `ee/surveys/summaries/headline_summary.py`). An explicit
 * `false` stays excluded: a survey switched from partial to non-partial keeps its old partials.
 */
export function getSurveyNotificationFilters(
    surveyId: string,
    enablePartialResponses: boolean,
    extraSentEventProperties: EventPropertyFilter[] = []
): CyclotronJobFiltersType {
    const surveyIdProperty: EventPropertyFilter = {
        key: SurveyEventProperties.SURVEY_ID,
        type: PropertyFilterType.Event,
        value: surveyId,
        operator: PropertyOperator.Exact,
    }
    const sentEventProperties: EventPropertyFilter[] = [
        surveyIdProperty,
        {
            key: SurveyEventProperties.SURVEY_COMPLETED,
            type: PropertyFilterType.Event,
            value: true,
            operator: PropertyOperator.Exact,
        },
        ...extraSentEventProperties,
    ]
    // Event entries are OR'd, so a second branch is how "absent or true" is expressed with
    // plain property filters rather than a hand-written HogQL predicate.
    const completedUnsetEventProperties: EventPropertyFilter[] = [
        surveyIdProperty,
        {
            key: SurveyEventProperties.SURVEY_COMPLETED,
            type: PropertyFilterType.Event,
            value: PropertyOperator.IsNotSet,
            operator: PropertyOperator.IsNotSet,
        },
        ...extraSentEventProperties,
    ]

    return {
        events: [
            {
                id: SurveyEventName.SENT,
                type: 'events',
                properties: sentEventProperties,
            },
            ...(enablePartialResponses
                ? []
                : [
                      {
                          id: SurveyEventName.SENT,
                          type: 'events' as const,
                          properties: completedUnsetEventProperties,
                      },
                  ]),
            {
                id: SurveyEventName.DISMISSED,
                type: 'events',
                properties: [
                    {
                        key: SurveyEventProperties.SURVEY_ID,
                        type: PropertyFilterType.Event,
                        value: surveyId,
                        operator: PropertyOperator.Exact,
                    },
                    {
                        key: SurveyEventProperties.SURVEY_PARTIALLY_COMPLETED,
                        type: PropertyFilterType.Event,
                        value: true,
                        operator: PropertyOperator.Exact,
                    },
                ],
            },
        ],
    }
}
